import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Filter } from 'lucide-react';
import TrackCard from '../components/TrackCard';
import { useAuth } from '../contexts/AuthContext';
import TierBadge from '../components/TierBadge';
import logger from '../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

const MusicPage = () => {
  const [tracks, setTracks] = useState([]);
  const [filteredTracks, setFilteredTracks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedMood, setSelectedMood] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const moods = ['Uplifting', 'Spiritual', 'Powerful', 'Chill'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tracksRes, genresRes] = await Promise.all([
          axios.get(`${API}/api/music/tracks`),
          axios.get(`${API}/api/music/genres`)
        ]);
        setTracks(tracksRes.data);
        setFilteredTracks(tracksRes.data);
        setGenres(genresRes.data);
      } catch (e) {
        logger.error('Failed to load music', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // API and axios are stable constants

  useEffect(() => {
    let filtered = [...tracks];
    
    if (selectedGenre) {
      filtered = filtered.filter(t => t.genre === selectedGenre);
    }
    if (selectedMood) {
      filtered = filtered.filter(t => t.mood === selectedMood);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.title.toLowerCase().includes(q) ||
        t.genre?.toLowerCase().includes(q) ||
        t.album?.toLowerCase().includes(q)
      );
    }
    
    setFilteredTracks(filtered);
  }, [tracks, selectedGenre, selectedMood, searchQuery]);

  const clearFilters = () => {
    setSelectedGenre('');
    setSelectedMood('');
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-32" data-testid="music-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {user?.subscription_tier && (
          <div className="mb-4">
            <TierBadge tier={user.subscription_tier} />
          </div>
        )}
        <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Stream & Purchase</p>
        <h1 className="font-['Bebas_Neue'] text-5xl md:text-6xl">MUSIC LIBRARY</h1>
      </div>

      {/* Filters */}
      <div className="sticky top-16 z-30 bg-[#0D0D0D]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]" />
              <input
                type="text"
                placeholder="Search tracks, genres, albums..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[#1A1A1A] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white placeholder-[#737373] transition-colors"
                data-testid="music-search"
              />
            </div>

            {/* Genre Filter */}
            <div className="relative">
              <select
                value={selectedGenre}
                onChange={(e) => setSelectedGenre(e.target.value)}
                className="appearance-none px-4 py-3 pr-10 bg-[#1A1A1A] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white cursor-pointer"
                data-testid="genre-filter"
              >
                <option value="">All Genres</option>
                {genres.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
              <Filter size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
            </div>

            {/* Mood Filter */}
            <div className="relative">
              <select
                value={selectedMood}
                onChange={(e) => setSelectedMood(e.target.value)}
                className="appearance-none px-4 py-3 pr-10 bg-[#1A1A1A] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white cursor-pointer"
                data-testid="mood-filter"
              >
                <option value="">All Moods</option>
                {moods.map(mood => (
                  <option key={mood} value={mood}>{mood}</option>
                ))}
              </select>
              <Filter size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
            </div>

            {(selectedGenre || selectedMood || searchQuery) && (
              <button
                onClick={clearFilters}
                className="px-4 py-3 text-sm text-[#D4AF37] hover:underline"
                data-testid="clear-filters"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tracks Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={`skeleton-${i}`} className="space-y-4">
                <div className="aspect-square skeleton" />
                <div className="h-4 skeleton w-3/4" />
                <div className="h-3 skeleton w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredTracks.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#A3A3A3]">No tracks found matching your filters.</p>
            <button onClick={clearFilters} className="mt-4 text-[#D4AF37] hover:underline">
              Clear all filters
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#737373] mb-6">{filteredTracks.length} tracks</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredTracks.map((track, idx) => (
                <div key={track.id} className="animate-fade-in-up" style={{ animationDelay: `${idx * 0.05}s` }}>
                  <TrackCard track={track} tracks={filteredTracks} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MusicPage;
